/**
 * Segmento de uma linha. Três formas:
 * - `{ chord: 'G', text: 'letra' }`: acorde sobre o início do texto (`[G]letra`);
 * - `{ chord: null, text }`: texto sem acorde (`bold` quando veio entre `*…*`);
 * - `{ chord: 'Gm', text: '', inline: true }`: acorde solto no meio da linha (`|Gm|`),
 *   mostrado na própria linha do texto (solos, passagens instrumentais).
 */
export type Segment = {
  chord: string | null;
  text: string;
  bold?: true;
  inline?: true;
};

export type Line = {
  segments: Segment[];
  /** Repetição no fim da linha (`*2x*` → `'2x'`). */
  repeat?: string;
  /** Linha em branco / `!!` antes dela, dentro da mesma seção (quebra de estrofe). */
  gapBefore?: true;
};

export type SectionKind = 'verso' | 'pre-refrao' | 'refrao' | 'outra';
export type Section = { name: string; kind: SectionKind; lines: Line[] };

/** `!!*REFRÃO*!!!!`, `!!!!*Verso*!!`, `!!*Intro*`: bangs opcionais dos dois lados. */
const HEADER = /^!+\*(.+?)\*!*$/;
const REPEAT = /\s*\*(\d+x)\*\s*$/i;
/** Acorde `[X]`, acorde solto `|X|` ou trecho em negrito `*texto*`, nessa ordem de tentativa. */
const TOKENS = /\[([^[\]\n]+)\]|\|([^|\n]+)\||\*([^*\n]+)\*/g;
const CHORD_MARKS = /\[([^[\]\n]+)\]|\|([^|\n]+)\|/g;
const NOTE = '[A-Ga-g][#b]?';
/** Raiz + (qualidade | número | `(extensão)` | alteração)* + baixo opcional. */
const CHORD = new RegExp(
  `^${NOTE}(?:maj|min|dim|aug|sus|add|m|M|°|º|ø|\\+|-|\\d+|\\([^)\\s]*\\)|[#b]\\d+|/\\d+)*(?:/${NOTE})?$`,
);
/** Marcas que aparecem entre colchetes sem serem acorde nem erro: `[(]`, `[)]`, `[-]`. */
const DECORATION = /^[()\-–]+$/;

/** Tolera maiúsculas, acentos e erros de digitação comuns (`Refão`, `Reffrão`, `REFRÂO`). */
export const sectionKind = (name: string): SectionKind => {
  const key = name
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toUpperCase()
    .trim();

  if (/^PRE/.test(key)) return 'pre-refrao';
  if (/^REF/.test(key)) return 'refrao';
  if (/^(VERSO|ESTROFE|PRIMEIRA PARTE|SEGUNDA PARTE)/.test(key)) return 'verso';
  return 'outra';
};

/** `G`, `Am7`, `A9/C#`, `N.C.`: raiz A–G com sufixo livre e baixo opcional. */
export const isChord = (token: string) => token === 'N.C.' || CHORD.test(token);

const upperRoot = (note: string) => note.charAt(0).toUpperCase() + note.slice(1);

/** `bM7` → `BM7`, `D9/f#` → `D9/F#`. Tokens que não são acorde ficam como vieram. */
export const normalizeChord = (text: string) =>
  text
    .trim()
    .split(/\s+/)
    .map((token) => {
      if (!CHORD.test(token)) return token;
      const [main = '', bass] = token.split('/');
      return bass === undefined
        ? upperRoot(main)
        : `${upperRoot(main)}/${upperRoot(bass)}`;
    })
    .join(' ');

/** Todos os tokens do trecho são acordes? (`|Solo|` não é; `|Gm Dm|` é.) */
const allChords = (text: string) => text.trim().split(/\s+/).every(isChord);

const parseSegments = (text: string): Segment[] => {
  const segments: Segment[] = [];
  let chord: string | null = null; // acorde esperando o texto que vem depois
  let cursor = 0;

  const pushText = (value: string, bold = false) => {
    if (!value) return;
    segments.push({ chord, text: value, ...(bold ? { bold: true as const } : {}) });
    chord = null;
  };
  const flushChord = () => {
    if (chord) segments.push({ chord, text: ' ' });
    chord = null;
  };

  for (const match of text.matchAll(TOKENS)) {
    const [raw, bracket, pipe, bold] = match;
    const index = match.index ?? 0;

    if (pipe !== undefined && !allChords(pipe)) continue; // `|texto|` segue como texto

    pushText(text.slice(cursor, index));
    cursor = index + raw.length;

    if (bracket !== undefined) {
      flushChord();
      chord = normalizeChord(bracket);
    } else if (pipe !== undefined) {
      flushChord();
      segments.push({ chord: normalizeChord(pipe), text: '', inline: true });
    } else {
      pushText(bold!, true);
    }
  }

  pushText(text.slice(cursor));
  flushChord();
  return segments;
};

/**
 * Converte o conteúdo (`[G]letra`, `!!*REFRÃO*!!!!`, `*2x*`) em seções e linhas.
 * Linha em branco ou `!!` isolado = quebra de estrofe dentro da seção. Conteúdo antes do
 * primeiro cabeçalho (ou música sem cabeçalho) cai numa seção sem nome.
 */
export const parseSong = (content: string): Section[] => {
  const sections: Section[] = [];
  let current: Section | undefined;
  let gap = false;

  const open = (name: string) => {
    const section: Section = { name, kind: sectionKind(name), lines: [] };
    sections.push(section);
    gap = false;
    return section;
  };

  for (const rawLine of content.split(/\r?\n/)) {
    // alguns dados antigos trazem a linha inteira entre crases
    const line = rawLine
      .trim()
      .replace(/^`(.*)`$/, '$1')
      .trim();

    if (!line || /^!+$/.test(line)) {
      gap = true;
      continue;
    }

    const header = line.match(HEADER);
    if (header) {
      current = open(header[1]!.trim());
      continue;
    }

    let text = line;
    let repeat: string | undefined;
    const repeatMatch = text.match(REPEAT);
    if (repeatMatch) {
      repeat = repeatMatch[1]!.toLowerCase();
      text = text.slice(0, repeatMatch.index);
    }

    const segments = parseSegments(text);

    // `*2x*` sozinho na linha vale para a linha anterior
    if (segments.length === 0) {
      const previous = current?.lines.at(-1);
      if (previous && repeat) previous.repeat = repeat;
      continue;
    }

    current ??= open('');
    const parsed: Line = { segments };
    if (repeat) parsed.repeat = repeat;
    if (gap && current.lines.length > 0) parsed.gapBefore = true;
    gap = false;
    current.lines.push(parsed);
  }

  return sections.filter((section) => section.lines.length > 0);
};

/**
 * Acordes únicos na ordem de aparição, já normalizados (`[G D]` e `|Gm|` contam; `|texto|`,
 * `[(]` e acordes malformados não). `map` permite transpor antes de deduplicar.
 */
export const chordsOf = (content: string, map: (chord: string) => string = (c) => c) => {
  const seen = new Set<string>();

  for (const match of content.matchAll(CHORD_MARKS)) {
    const text = (match[1] ?? match[2])!;
    if (match[2] !== undefined && !allChords(text)) continue;

    for (const chord of normalizeChord(text).split(/\s+/)) {
      if (isChord(chord)) seen.add(map(chord));
    }
  }

  return [...seen];
};

export type ContentIssue = {
  /** 1-based. */
  line: number;
  type: 'unbalanced-bracket' | 'unknown-chord';
  message: string;
};

/** Problemas que o editor pode avisar: colchetes soltos e acordes que não se reconhecem. */
export const findContentIssues = (content: string): ContentIssue[] => {
  const issues: ContentIssue[] = [];

  content.split(/\r?\n/).forEach((line, index) => {
    const number = index + 1;
    const opens = (line.match(/\[/g) || []).length;
    const closes = (line.match(/\]/g) || []).length;

    if (opens !== closes) {
      issues.push({
        line: number,
        type: 'unbalanced-bracket',
        message: 'Colchete sem par',
      });
    }

    for (const match of line.matchAll(/\[([^[\]\n]+)\]/g)) {
      for (const chord of match[1]!.trim().split(/\s+/)) {
        if (!isChord(chord) && !DECORATION.test(chord)) {
          issues.push({
            line: number,
            type: 'unknown-chord',
            message: `Acorde não reconhecido: ${chord}`,
          });
        }
      }
    }
  });

  return issues;
};
